import { File, Trash2, Upload } from "lucide-react";
import type { FC } from "react";

import { AdminSections } from "@/api/admin/teamRoles";
import { CustomButton } from "@/components/atoms";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { usePermission } from "@/services/casl/usePermission";

interface Props {
  fileName: string;
  isModalOpen: boolean;
  setIsModalOpen: (value: boolean) => void;
  handleResetMenu: () => void | Promise<void>;
  isResettingMenu?: boolean;
}

const SummaryBar: FC<Props> = ({
  fileName,
  isModalOpen,
  setIsModalOpen,
  handleResetMenu,
  isResettingMenu = false,
}) => {
  const hasUpdatePermission = usePermission(AdminSections.BRAND, {
    isUpdate: true,
  });

  return (
    <>
      <div className="flex items-center justify-between gap-3 p-4 border border-yellow-400/30 rounded-lg bg-[#fefdf7]">
        <div className="flex items-center gap-3">
          <div className="border rounded-md p-3 bg-white">
            <File className="w-4 h-4" />
          </div>
          <div>
            <p className="font-bold text-base mb-1 break-all">{fileName}</p>
            <p className="text-sm text-gray-500">
              Imported by Spice AI · review and edit below
            </p>
          </div>
        </div>
        <CustomButton
          variant="light"
          onClick={e => {
            e.preventDefault();
            setIsModalOpen(true);
          }}
          disabled={!hasUpdatePermission}
        >
          <Upload className="w-4 h-4" />
          Change menu
        </CustomButton>
      </div>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="sm:max-w-[420px]">
          <div className="flex flex-col items-center text-center">
            <div className="p-3 bg-red-50 rounded-full mb-4">
              <Upload className="w-6 h-6 text-red-600" />
            </div>
            <h2 className="text-xl font-bold mb-2">Change your menu?</h2>
            <div className="text-gray-500 mb-6 text-sm">
              Uploading a new file or link will
              <strong className="px-1 text-gray-950">
                clear this entire menu.
              </strong>
              <p>
                Every category, dish, photo, and branch setting will be removed.
                This action can&apos;t be undone.
              </p>
            </div>
            <div className="flex gap-3 w-full">
              <CustomButton
                variant="light"
                className="flex-1"
                onClick={() => setIsModalOpen(false)}
              >
                Keep this menu
              </CustomButton>
              <CustomButton
                variant="destructive"
                className="flex-1 items-center"
                onClick={handleResetMenu}
                disabled={isResettingMenu}
                isPending={isResettingMenu}
              >
                <Trash2 className="w-4 h-4" />
                Clear & re-upload
              </CustomButton>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SummaryBar;
